const MAX_GIFT_RECIPIENT_PROFILE_IMAGES = 10;
function safeCount(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.trunc(value));
}
function safeName(value) {
    return value.trim().slice(0, 100) || 'Unknown viewer';
}
function text(value, options = {}) {
    return {
        type: 'text',
        text: value,
        alignment: options.alignment ?? 'center',
        size: options.size ?? 'normal',
        emphasis: options.emphasis ?? 'normal',
    };
}
function profileBlock(subject, person, scale) {
    if (!person ||
        !person.profileImageAvailable ||
        typeof person.twitchUserId !== 'string' ||
        person.twitchUserId.trim().length < 1) {
        return null;
    }
    return {
        type: 'profile-image',
        subject,
        twitchUserId: person.twitchUserId,
        displayName: safeName(person.displayName),
        scale,
        alignment: 'center',
    };
}
function base(kind) {
    return {
        schemaVersion: 1,
        kind,
        paper: {
            nominalWidthMm: 80,
            printableWidth: 'driver-printable-area',
            topFeed: 'minimal',
            bottomFeed: 'tear-clearance-only',
        },
        imagePolicy: {
            mode: 'server-resolved-twitch-profile',
            monochrome: true,
            dither: true,
            externalUrlsInPrinterPayload: false,
        },
        blocks: [
            {
                type: 'wordmark',
                asset: 'ffscontrol-monochrome',
                alignment: 'center',
            },
            { type: 'separator' },
        ],
    };
}
function addTimestamp(document, occurredAt) {
    document.blocks.push({ type: 'separator' }, {
        type: 'timestamp',
        iso: occurredAt,
        alignment: 'center',
        size: 'small',
        locale: 'en-GB',
    });
}
export function composePrinterReceipt(event) {
    const document = base(event.kind);
    if (event.kind === 'follow') {
        document.blocks.push(text('NEW FOLLOWER', { size: 'large', emphasis: 'bold' }), text(safeName(event.follower.displayName), {
            size: 'x-large',
            emphasis: 'bold',
        }));
        addTimestamp(document, event.occurredAt);
        return document;
    }
    if (event.kind === 'subscription') {
        const profile = profileBlock('primary', event.subscriber, 'full-width');
        if (profile)
            document.blocks.push(profile);
        document.blocks.push(text(safeName(event.subscriber.displayName), {
            size: 'x-large',
            emphasis: 'bold',
        }), text('SUBSCRIPTION', { size: 'large', emphasis: 'bold' }), text(event.tierLabel, { size: 'normal', emphasis: 'bold' }));
        if (event.months !== null && safeCount(event.months) > 0) {
            document.blocks.push(text(`${safeCount(event.months)} month${safeCount(event.months) === 1 ? '' : 's'}`));
        }
        if (event.message?.trim()) {
            document.blocks.push(text(event.message.trim().slice(0, 500), { alignment: 'left' }));
        }
        addTimestamp(document, event.occurredAt);
        return document;
    }
    if (event.kind === 'bits') {
        const profile = profileBlock('primary', event.cheerer, 'small');
        if (profile)
            document.blocks.push(profile);
        document.blocks.push(text(event.cheerer ? safeName(event.cheerer.displayName) : 'Anonymous', {
            size: 'large',
            emphasis: 'bold',
        }), text(`${safeCount(event.bits).toLocaleString('en-GB')} BITS`, {
            size: 'x-large',
            emphasis: 'bold',
        }));
        if (event.message?.trim()) {
            document.blocks.push(text(event.message.trim().slice(0, 500), { alignment: 'left' }));
        }
        addTimestamp(document, event.occurredAt);
        return document;
    }
    if (event.kind === 'raid') {
        const profile = profileBlock('primary', event.raider, 'full-width');
        if (profile)
            document.blocks.push(profile);
        document.blocks.push(text(safeName(event.raider.displayName), {
            size: 'x-large',
            emphasis: 'bold',
        }), text('RAID', { size: 'large', emphasis: 'bold' }), text(`${safeCount(event.viewers).toLocaleString('en-GB')} viewer${safeCount(event.viewers) === 1 ? '' : 's'}`, {
            size: 'large',
            emphasis: 'bold',
        }));
        addTimestamp(document, event.occurredAt);
        return document;
    }
    const giftCount = event.recipients.length;
    const gifterProfile = profileBlock('gifter', event.gifter, 'small');
    if (gifterProfile)
        document.blocks.push(gifterProfile);
    document.blocks.push(text(event.gifter ? safeName(event.gifter.displayName) : 'Anonymous', {
        size: 'large',
        emphasis: 'bold',
    }), text(`GIFTED ${giftCount.toLocaleString('en-GB')} SUB${giftCount === 1 ? '' : 'S'}`, {
        size: 'x-large',
        emphasis: 'bold',
    }));
    if (giftCount <= MAX_GIFT_RECIPIENT_PROFILE_IMAGES) {
        const imageRecipients = event.recipients
            .filter((recipient) => recipient.profileImageAvailable &&
            typeof recipient.twitchUserId === 'string' &&
            recipient.twitchUserId.trim().length > 0)
            .map((recipient) => ({
            twitchUserId: recipient.twitchUserId,
            displayName: safeName(recipient.displayName),
        }));
        if (imageRecipients.length > 0) {
            document.blocks.push({
                type: 'profile-grid',
                subject: 'gift-recipients',
                columns: 2,
                showNames: true,
                scale: 'small',
                items: imageRecipients,
            });
        }
        const namesWithoutImages = event.recipients
            .filter((recipient) => !recipient.profileImageAvailable ||
            typeof recipient.twitchUserId !== 'string' ||
            recipient.twitchUserId.trim().length < 1)
            .map((recipient) => safeName(recipient.displayName));
        if (namesWithoutImages.length > 0) {
            document.blocks.push({
                type: 'name-list',
                subject: 'gift-recipients',
                names: namesWithoutImages,
                columns: 2,
                size: 'small',
            });
        }
    }
    else {
        document.blocks.push({
            type: 'name-list',
            subject: 'gift-recipients',
            names: event.recipients.map((recipient) => safeName(recipient.displayName)),
            columns: 2,
            size: 'small',
        });
    }
    addTimestamp(document, event.occurredAt);
    return document;
}
const SAMPLE_TIME = '2026-08-26T08:42:00.000Z';
function samplePerson(id, displayName) {
    return {
        twitchUserId: id,
        displayName,
        profileImageAvailable: true,
    };
}
export function printerReceiptPreviewCatalog() {
    const smallRecipients = Array.from({ length: 4 }, (_, index) => samplePerson(`recipient-${index + 1}`, `GiftedViewer${index + 1}`));
    const largeRecipients = Array.from({ length: 12 }, (_, index) => samplePerson(`recipient-${index + 1}`, `GiftedViewer${index + 1}`));
    return [
        {
            id: 'follow',
            label: 'Follow — text only',
            document: composePrinterReceipt({
                kind: 'follow',
                follower: samplePerson('follow-1', 'NewFollower'),
                occurredAt: SAMPLE_TIME,
            }),
        },
        {
            id: 'subscription',
            label: 'Single subscription — full-width profile',
            document: composePrinterReceipt({
                kind: 'subscription',
                subscriber: samplePerson('sub-1', 'SubscriberName'),
                tierLabel: 'Tier 1',
                months: 8,
                message: 'Eight months already. For fuck sake.',
                occurredAt: SAMPLE_TIME,
            }),
        },
        {
            id: 'bits',
            label: 'Bits — small profile',
            document: composePrinterReceipt({
                kind: 'bits',
                cheerer: samplePerson('bits-1', 'CheererName'),
                bits: 1500,
                message: 'A sample cheer message that can wrap across the receipt width.',
                occurredAt: SAMPLE_TIME,
            }),
        },
        {
            id: 'raid',
            label: 'Raid — full-width profile',
            document: composePrinterReceipt({
                kind: 'raid',
                raider: samplePerson('raid-1', 'RaiderName'),
                viewers: 42,
                occurredAt: SAMPLE_TIME,
            }),
        },
        {
            id: 'gift-subs-4',
            label: '4 gifted subs — gifter + recipient profiles',
            document: composePrinterReceipt({
                kind: 'gift-subs',
                gifter: samplePerson('gifter-1', 'GenerousGifter'),
                recipients: smallRecipients,
                occurredAt: SAMPLE_TIME,
            }),
        },
        {
            id: 'gift-subs-12',
            label: '12 gifted subs — gifter profile + names only',
            document: composePrinterReceipt({
                kind: 'gift-subs',
                gifter: samplePerson('gifter-1', 'GenerousGifter'),
                recipients: largeRecipients,
                occurredAt: SAMPLE_TIME,
            }),
        },
    ];
}
//# sourceMappingURL=printer-receipt.js.map